import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import type { DraftBlock } from 'lib/community/discussions-writer'
import { blockSummary, dropIndex } from 'lib/community/writer-phone'
import styles from './Writer.module.css'

interface ReorderListProps {
  blocks: DraftBlock[]
  /** Drop block `id` at `index` (the reducer's `moveBlockTo`). */
  onMoveTo: (id: number, index: number) => void
  onRemove: (block: DraftBlock) => void
}

/**
 * The phone writer's reorder mode (toolbar ⇅; "Done" in the top bar ends it): one row per block —
 * a drag handle, what the block is, ▲ ▼ and ✕, every target 44px. The handle drags with any
 * pointer (touch, pen, mouse: pointer events, `touch-action: none` on the handle so the page does
 * not scroll instead; the moves are read on window — a row React moves loses pointer capture); ▲ ▼ do the same one step at a time for a keyboard or a
 * screen reader. A drag moves the block as the finger crosses a neighbour's middle, so the row
 * stays under the finger. Long-press to start a drag is v2.
 */
const ReorderList: React.VFC<ReorderListProps> = ({ blocks, onMoveTo, onRemove }) => {
  const listRef = useRef<HTMLOListElement>(null)
  const [dragId, setDragId] = useState<number | null>(null)
  const blocksRef = useRef(blocks)
  blocksRef.current = blocks

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>, id: number): void => {
    if (e.button !== 0) {
      return
    }
    e.preventDefault()
    setDragId(id)
  }

  const moveTo = useRef(onMoveTo)
  moveTo.current = onMoveTo
  useEffect(() => {
    if (dragId === null) {
      return undefined
    }
    const onMove = (e: PointerEvent): void => {
      const list = listRef.current
      if (!list) {
        return
      }
      const rows = Array.from(list.querySelectorAll<HTMLElement>('[data-block-id]'))
      const mids = rows.map(row => {
        const r = row.getBoundingClientRect()
        return r.top + r.height / 2
      })
      const from = blocksRef.current.findIndex(b => b.id === dragId)
      const to = dropIndex(mids, e.clientY, from)
      if (from >= 0 && to !== from) {
        moveTo.current(dragId, to)
      }
    }
    const onEnd = (): void => setDragId(null)
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onEnd)
    window.addEventListener('pointercancel', onEnd)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onEnd)
      window.removeEventListener('pointercancel', onEnd)
    }
  }, [dragId])

  return (
    <ol ref={listRef} className={styles.reorder} aria-label="Reorder blocks">
      {blocks.map((block, i) => {
        const { kind, text } = blockSummary(block)
        return (
          <li
            key={block.id}
            data-block-id={block.id}
            className={clsx(styles.reorderRow, { [styles.reorderDragging]: dragId === block.id })}
          >
            <button
              type="button"
              className={styles.reorderHandle}
              aria-label={`Drag ${kind.toLowerCase()} block`}
              tabIndex={-1}
              onPointerDown={e => onPointerDown(e, block.id)}
            >
              <span aria-hidden="true">{'⠿'}</span>
            </button>
            <span className={styles.reorderText}>
              <span className={styles.reorderKind}>{kind}</span>
              <span className={styles.reorderBody}>{text}</span>
            </span>
            <button
              type="button"
              className={styles.reorderTool}
              aria-label={`Move ${kind.toLowerCase()} block up`}
              disabled={i === 0}
              onClick={() => onMoveTo(block.id, i - 1)}
            >
              <span aria-hidden="true">{'▲'}</span>
            </button>
            <button
              type="button"
              className={styles.reorderTool}
              aria-label={`Move ${kind.toLowerCase()} block down`}
              disabled={i === blocks.length - 1}
              onClick={() => onMoveTo(block.id, i + 1)}
            >
              <span aria-hidden="true">{'▼'}</span>
            </button>
            <button
              type="button"
              className={clsx(styles.reorderTool, styles.reorderRemove)}
              aria-label={`Remove ${kind.toLowerCase()} block`}
              onClick={() => onRemove(block)}
            >
              <span aria-hidden="true">{'✕'}</span>
            </button>
          </li>
        )
      })}
    </ol>
  )
}

export default ReorderList
